// Cliente Drive REST mínimo, só read. Usa o token de drive-auth.

import { getAccessToken } from "./drive-auth";

const DRIVE_API_BASE = "https://www.googleapis.com/drive/v3";
const FILE_FIELDS = "id,name,mimeType,modifiedTime,iconLink,webViewLink,size";

export type DriveFile = {
  id: string;
  name: string;
  mimeType: string;
  modifiedTime: string;
  iconLink?: string;
  webViewLink?: string;
  size?: string;
};

async function authorizedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return fetch(url, { ...init, headers });
}

async function expectOk(response: Response, context: string): Promise<Response> {
  if (response.ok) return response;
  let message = `${response.status} ${response.statusText}`;
  try {
    const body = await response.json();
    if (body?.error?.message) message = body.error.message;
  } catch {
    // mantém status text
  }
  throw new Error(`Drive ${context} falhou: ${message}`);
}

export async function getFileMetadata(fileId: string): Promise<DriveFile> {
  const response = await authorizedFetch(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?fields=${encodeURIComponent(FILE_FIELDS)}&supportsAllDrives=true`,
  );
  await expectOk(response, "files.get(metadata)");
  return (await response.json()) as DriveFile;
}

export async function downloadFileText(fileId: string): Promise<string> {
  const response = await authorizedFetch(
    `${DRIVE_API_BASE}/files/${encodeURIComponent(fileId)}?alt=media&supportsAllDrives=true`,
  );
  await expectOk(response, "files.get(media)");
  return response.text();
}

/**
 * Busca HTMLs em todo o Drive do usuário.
 * - `mimeType = 'text/html'` cobre uploads que o Drive identificou como HTML.
 * - `fileExtension = 'html'|'htm'` cobre arquivos com mime octet-stream.
 * - Se `query` vazio, devolve os HTMLs mais recentes.
 */
export async function searchHtmlFiles(query: string, pageSize = 30): Promise<DriveFile[]> {
  const trimmed = query.trim();
  const safe = trimmed.replaceAll("\\", "\\\\").replaceAll("'", "\\'");
  const filters = [
    "trashed = false",
    "(mimeType = 'text/html' or fileExtension = 'html' or fileExtension = 'htm')",
  ];
  if (safe.length > 0) filters.push(`name contains '${safe}'`);
  const params = new URLSearchParams({
    q: filters.join(" and "),
    fields: `files(${FILE_FIELDS})`,
    pageSize: String(pageSize),
    orderBy: "modifiedTime desc",
    spaces: "drive",
    supportsAllDrives: "true",
    includeItemsFromAllDrives: "true",
  });
  const response = await authorizedFetch(`${DRIVE_API_BASE}/files?${params}`);
  await expectOk(response, "files.list(search)");
  const body = (await response.json()) as { files?: DriveFile[] };
  return body.files ?? [];
}

/** Extrai um fileId de URLs do Drive ou retorna a própria string se já parecer um ID. */
export function extractFileId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  // URLs comuns: /file/d/<id>/, /open?id=<id>, /document/d/<id>
  const urlMatch = trimmed.match(/[-\w]{25,}/);
  return urlMatch ? urlMatch[0] : null;
}
